<script setup lang="ts">
import { computed, ref } from 'vue';
import { Text } from '@sitecore-jss/sitecore-jss-vue';
import Modal from '@/elements/Modal.vue';
import SiteImage from '@/elements/SiteImage.vue';
import { settingValue } from '@/utils/site-utils';
import SiteLink from '@/elements/SiteLink.vue';
import type { FooterProps } from '@/types/components/Footer';

const props = defineProps<FooterProps>();
const newsletterModalRef = ref<InstanceType<typeof Modal> | null>(null);
const newsletterCaptchaValue = ref('');
const newsletterCaptchaError = ref('');
const newsletterCaptchaCode = '8290';

const footerStyle = computed(() => ({
  backgroundColor: settingValue(props.fields?.backColor),
  color: settingValue(props.fields?.foreColor),
}));
const contactMenuItem = computed(() =>
  props.fields?.menuList?.find((item) => !item.fields?.childList?.length)
);
const menuItems = computed(() =>
  (props.fields?.menuList ?? []).filter((item) => item.fields?.childList?.length)
);
const opensInNewWindow = (link: any) => link?.value?.target === '_blank';
const getLinkHref = (link: any) => link?.value?.href || '#';
const hasLogoSvg = computed(() => Boolean(props.fields?.logo?.value?.svgCode));

const newsletterCaptchaImage = computed(() => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="106" height="56" viewBox="0 0 106 56">
    <rect width="106" height="56" rx="7" fill="#f8f8f8"/>
    <path d="M-6 38 C16 8, 42 52, 66 18 S98 32, 112 8" fill="none" stroke="#58a843" stroke-width="1.2" opacity=".8"/>
    <path d="M0 18 L106 40" stroke="#7fb85e" stroke-width="1" opacity=".7"/>
    <path d="M12 50 L98 6" stroke="#884fc7" stroke-width="1" opacity=".55"/>
    <text x="53" y="38" text-anchor="middle" fill="#7c8e34" font-size="31" font-family="Georgia, serif" font-style="italic" font-weight="700" transform="rotate(-12 53 30)">${newsletterCaptchaCode}</text>
    <g fill="#58a843" opacity=".75">
      <circle cx="14" cy="15" r="1"/><circle cx="31" cy="42" r="1"/><circle cx="49" cy="12" r="1"/>
      <circle cx="70" cy="45" r="1"/><circle cx="88" cy="17" r="1"/>
    </g>
  </svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
});

const openNewsletterModal = () => {
  newsletterCaptchaValue.value = '';
  newsletterCaptchaError.value = '';
  newsletterModalRef.value?.open();
};

const closeNewsletterModal = () => {
  newsletterCaptchaValue.value = '';
  newsletterCaptchaError.value = '';
  newsletterModalRef.value?.close();
};

const onNewsletterCaptchaInput = () => {
  newsletterCaptchaError.value = '';
};

const confirmNewsletterCaptcha = () => {
  if (newsletterCaptchaValue.value.trim() !== newsletterCaptchaCode) {
    newsletterCaptchaError.value = 'Please enter the security code shown on the right.';
    return;
  }

  closeNewsletterModal();
};
</script>

<template>
  <footer class="c-footer" :style="footerStyle" v-if="fields">
    <ContentGrid :desktop-default-grid="[1, 22]" :mobile-default-grid="[1, 14]">
      <div class="c-footer__container">
        <!-- Logo -->
        <Icon v-if="hasLogoSvg" class="c-footer__logo" :field="fields.logo" />
        <SiteImage
          v-else-if="fields.logo"
          class="c-footer__logo"
          :field="fields.logo"
          loading="eager"
        />

        <!-- 主菜单区域 -->
        <div class="c-footer__menu">
          <div class="c-footer__menu-column" v-for="item in menuItems" :key="item.id">
            <div class="c-footer__menu-title">
              <Text :field="item.fields.title" />
            </div>
            <div class="c-footer__menu-items" v-if="item.fields?.childList?.length">
              <template v-for="child in item.fields.childList" :key="child.id">
                <a
                  v-if="opensInNewWindow(child.fields.link)"
                  :href="getLinkHref(child.fields.link)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="c-footer__menu-link"
                >
                  <Text :field="child.fields.text" />
                </a>
                <SiteLink v-else :link="child.fields.link" class="c-footer__menu-link">
                  <Text :field="child.fields.text" />
                </SiteLink>
              </template>
            </div>
          </div>
          <div class="c-footer__menu-column" v-if="fields?.contactList?.length">
            <div v-if="contactMenuItem?.fields?.title" class="c-footer__menu-title">
              <Text :field="contactMenuItem.fields.title" />
            </div>
            <div class="c-footer__contact-grid">
              <div
                class="c-footer__contact-column"
                v-for="contact in fields.contactList"
                :key="contact.id"
              >
                <div class="c-footer__contact-label">
                  <Text :field="contact.fields.title" />
                </div>
                <div class="c-footer__contact-list" v-if="contact.fields?.contactItems?.length">
                  <div
                    v-for="contactItem in contact.fields.contactItems"
                    :key="contactItem.id"
                    class="c-footer__contact-row"
                  >
                    <span class="c-footer__contact-region" v-if="contactItem.fields?.region?.value">
                      {{ contactItem.fields.region.value }}
                    </span>
                    <SiteLink
                      v-if="contactItem.fields?.link"
                      :link="contactItem.fields.link"
                      class="c-footer__contact-link"
                    >
                      <Text :field="contactItem.fields.value" />
                    </SiteLink>
                    <span v-else class="c-footer__contact-link">
                      <Text :field="contactItem.fields.value" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="c-footer__extra"
          v-if="fields?.newsletterTitle?.value || fields?.socialMediaList?.length"
        >
          <!-- <div class="c-footer__newsletter" v-if="fields?.newsletterTitle?.value">
            <div class="c-footer__newsletter-title">
              <Text :field="fields.newsletterTitle" />
            </div>
            <form class="c-footer__newsletter-form" @submit.prevent="openNewsletterModal">
              <input
                type="email"
                class="c-footer__newsletter-input"
                :placeholder="fields.newsletterPlaceholder?.value || 'Email'"
                required
              />
              <button type="submit" class="c-footer__newsletter-button">
                {{ fields.newsletterButtonText?.value || 'Subscribe' }}
              </button>
            </form>
            <label class="c-footer__newsletter-privacy" v-if="fields?.newsletterPrivacyText?.value">
              <input type="checkbox" class="c-footer__newsletter-checkbox" required />
              <span class="c-footer__newsletter-privacy-text">
                {{ fields.newsletterPrivacyText.value }}
              </span>
            </label>
          </div> -->

          <div class="c-footer__social-wrapper" v-if="fields?.socialMediaList?.length">
            <SiteLink
              v-for="social in fields.socialMediaList"
              :key="social.id"
              :link="social.fields.link"
              class="c-footer__social-link"
              :aria-label="social.fields.name?.value"
            >
              <icon :field="social.fields.icon" class="c-footer__social-icon" />
            </SiteLink>
          </div>
        </div>

        <!-- 底部区域 -->
        <div
          class="c-footer__bottom"
          v-if="fields?.copyright?.value || fields?.bottomLinks?.length"
        >
          <div class="c-footer__copyright" v-if="fields?.copyright?.value">
            <Text :field="fields.copyright" />
          </div>
          <div class="c-footer__bottom-links" v-if="fields?.bottomLinks?.length">
            <SiteLink
              v-for="link in fields.bottomLinks"
              :key="link.id"
              :link="link.fields.link"
              class="c-footer__bottom-link"
            >
              <Text :field="link.fields.text" />
            </SiteLink>
          </div>
        </div>
      </div>
    </ContentGrid>

    <Modal
      ref="newsletterModalRef"
      class="c-footer-newsletter-modal"
      content-class="c-footer-newsletter-modal__modal"
      :closable="false"
      :sticky="false"
      :is-full="true"
    >
      <div class="c-footer-newsletter-modal__panel">
        <button
          class="c-footer-newsletter-modal__close"
          type="button"
          aria-label="Close"
          @click="closeNewsletterModal"
        >
          <Icon name="close" size="small" />
        </button>

        <h2>Email Security Verification</h2>
        <p class="c-footer-newsletter-modal__description">
          To ensure subscription security, please enter the security code for verification.
        </p>

        <div class="c-footer-newsletter-modal__captcha">
          <div
            :class="[
              'c-footer-newsletter-modal__captcha-field',
              { 'has-error': Boolean(newsletterCaptchaError) },
            ]"
          >
            <input
              v-model="newsletterCaptchaValue"
              type="text"
              inputmode="text"
              autocomplete="off"
              autocapitalize="none"
              spellcheck="false"
              placeholder="security code on the right"
              aria-label="Security code"
              @input="onNewsletterCaptchaInput"
            />
          </div>
          <img
            class="c-footer-newsletter-modal__captcha-image"
            :src="newsletterCaptchaImage"
            alt="Security code"
          />
          <p v-if="newsletterCaptchaError" class="c-footer-newsletter-modal__captcha-error">
            {{ newsletterCaptchaError }}
          </p>
        </div>

        <button
          class="c-footer-newsletter-modal__submit"
          type="button"
          @click="confirmNewsletterCaptcha"
        >
          Sure
        </button>
      </div>
    </Modal>
  </footer>
</template>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-footer {
  background-color: #151b17;
  color: $white;
  padding: 30px 0 20px;

  &__container {
    width: 100%;
    max-width: 1512px;
    margin: 0 auto;
  }

  &__logo {
    display: block;
    width: 92px;
    height: auto;
    margin-bottom: 20px;

    svg,
    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }

  &__menu {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__menu-column {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__menu-title {
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.28);
    color: $white;
    font-size: 14px;
    line-height: 1.2;
  }

  &__menu-items {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__menu-link {
    color: rgba(255, 255, 255, 0.84);
    font-size: 14px;
    font-weight: 400;
    line-height: 1.35;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: $white;
    }
  }

  &__contact-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__contact-column {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__contact-label {
    color: #d6c8aa;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: 0;
  }

  &__contact-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__contact-row {
    display: grid;
    grid-template-columns: 45px minmax(0, 1fr);
    gap: 6px;
    align-items: baseline;
    font-size: 14px;
    line-height: 1.35;
  }

  &__contact-region {
    color: rgba(255, 255, 255, 0.5);
    font-size: inherit;
    font-weight: 400;
  }

  &__contact-link {
    min-width: 0;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 400;
    overflow-wrap: anywhere;
    text-decoration: none;

    &:hover {
      color: $white;
    }
  }

  &__extra {
    display: flex;
    gap: 10px;
    margin-top: 24px;
    max-width: 560px;
  }

  &__newsletter {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  &__newsletter-title {
    color: $white;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.2;
  }

  &__newsletter-form {
    display: flex;
    gap: 6px;
    width: 100%;
    max-width: 250px;
  }

  &__newsletter-input {
    flex: 1;
    min-width: 0;
    padding: 4px 8px;
    background-color: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.48);
    border-radius: 4px;
    color: $white;
    font-size: 14px;
    font-weight: 400;
    line-height: 23px;
    transition:
      border-color 0.3s ease,
      background-color 0.3s ease;

    &::placeholder {
      color: rgba(255, 255, 255, 0.42);
    }

    &:focus {
      outline: none;
      border-color: rgba(255, 255, 255, 0.75);
      background-color: rgba(255, 255, 255, 0.06);
    }
  }

  &__newsletter-button {
    flex: 0 0 auto;
    padding: 0 10px;
    background-color: #d6c8aa;
    border: none;
    border-radius: 4px;
    color: #151b17;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    line-height: 23px;
    white-space: nowrap;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: #e0d3ba;
    }
  }

  &__newsletter-privacy {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    color: rgba(255, 255, 255, 0.48);
    cursor: pointer;
    font-size: 12px;
    line-height: 1.45;
  }

  &__newsletter-checkbox {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 12px;
    height: 12px;
    margin-top: 1px;
    border: 1px solid rgba(255, 255, 255, 0.45);
    border-radius: 2px;
    background-color: transparent;
    cursor: pointer;
    position: relative;

    &:checked {
      background-color: #d6c8aa;
      border-color: #d6c8aa;

      &::after {
        content: '';
        position: absolute;
        left: 2px;
        top: 0;
        width: 4px;
        height: 6px;
        border: solid #151b17;
        border-width: 0 1px 1px 0;
        transform: rotate(45deg);
      }
    }

    &:focus {
      outline: 2px solid rgba(214, 200, 170, 0.3);
      outline-offset: 2px;
    }
  }

  &__newsletter-privacy-text {
    font-weight: 300;
    line-height: 1.5;
  }

  &__social-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  &__social-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.86);
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: $white;
    }
  }

  &__social-icon {
    display: block;
    width: 96px;
    height: 20px;

    img,
    svg {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 9px;
    margin-top: 30px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.22);
    font-size: 14px;
    line-height: 1.35;
  }

  &__copyright {
    font-size: inherit;
    font-weight: 400;
  }

  &__bottom-links {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__bottom-link {
    font-size: inherit;
    font-weight: 400;
    line-height: inherit;
    text-decoration: none;

    &:hover {
      color: rgba(255, 255, 255, 0.85);
    }
  }

  @include tablet {
    &__menu {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 44px 54px;
    }

    &__bottom {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }

    &__bottom-links {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 0;
    }

    &__bottom-link {
      &:not(:last-child)::after {
        content: '|';
        margin: 0 7px;
      }
    }
  }

  @include desktop {
    &__logo {
      width: 141px;
    }

    &__menu {
      grid-template-columns: 1.05fr 1fr 1.18fr 1.35fr;
      gap: 36px;
    }

    &__menu-column {
      gap: 10px;
    }

    &__menu-title {
      padding-bottom: 12px;
      font-size: 14px;
    }

    &__menu-items {
      gap: 8px;
    }

    &__menu-link {
      font-size: 14px;
      line-height: 1.24;
    }

    &__contact-grid {
      gap: 6px;
    }

    &__contact-column {
      gap: 6px;
    }

    &__contact-label {
      font-size: 14px;
    }

    &__contact-list {
      gap: 6px;
    }

    &__contact-row {
      grid-template-columns: 92px minmax(0, 1fr);
      gap: 6px;
      font-size: 14px;
      line-height: 1.45;
    }

    &__extra {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      justify-content: space-between;
      gap: 48px;
      margin-top: -60px;
      max-width: 480px;
    }

    &__newsletter-title {
      font-size: 14px;
    }

    &__newsletter-form {
      gap: 11px;
      max-width: none;
    }

    &__newsletter-input {
      flex: 0 0 260px;
      height: 42px;
      padding: 0 16px;
      font-size: 14px;
      line-height: 42px;
    }

    &__newsletter-button {
      height: 42px;
      padding: 0 12px;
      font-size: 14px;
      line-height: 42px;
    }

    &__newsletter-privacy {
      gap: 9px;
      max-width: 380px;
      font-size: 12px;
      line-height: 1.45;
    }

    &__newsletter-checkbox {
      width: 15px;
      height: 15px;
      min-width: 15px;
      margin-top: 1px;

      &:checked::after {
        left: 5px;
        top: 2px;
        width: 4px;
        height: 8px;
      }
    }

    &__social-icon {
      width: 96px;
      height: 20px;
    }

    &__extra {
      .c-footer__social-wrapper {
        margin-top: 24px;
      }
    }

    &__bottom {
      flex-direction: row;
      gap: 28px;
      align-items: center;
      justify-content: flex-start;
      margin-top: 28px;
      padding-top: 25px;
      font-size: 14px;
    }

    &__bottom-links {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 0;
    }

    &__bottom-link {
      &:not(:last-child)::after {
        content: '|';
        margin: 0 10px;
        color: rgba(255, 255, 255, 0.35);
      }
    }
  }

  @include desktop-large {
    &__container {
      max-width: 1536px;
    }

    &__menu {
      gap: 28px;
    }
  }
}

.c-footer-newsletter-modal {
  .e-modal__mask {
    background: rgba(0, 0, 0, 0.62);
    backdrop-filter: none;
  }

  .e-modal__content.c-footer-newsletter-modal__modal {
    width: min(100% - 32px, 468px);
    max-height: calc(100vh - 80px);
    border-radius: 14px;
    background: $white;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.2);
  }

  .e-modal__content-detail {
    max-height: calc(100vh - 128px);
    padding: 0;
  }

  &__panel {
    position: relative;
    padding: 24px 22px 23px;
    color: #3f3f46;
  }

  &__close {
    position: absolute;
    top: 23px;
    right: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 0;
    background: transparent;
    color: #3f3f46;
    cursor: pointer;
  }

  &__title {
    margin: 0;
    padding-right: 42px;
    color: #3f3f46;
    font-size: 19px;
    font-weight: 700;
    line-height: 1.3;
  }

  &__description {
    margin: 28px 0 14px;
    max-width: 388px;
    color: #3f3f46;
    font-size: 15px;
    line-height: 1.45;
    letter-spacing: 0.01em;
  }

  &__captcha {
    display: grid;
    grid-template-columns: minmax(0, 281px) 106px;
    gap: 15px;
    align-items: start;
  }

  &__captcha-field {
    display: flex;
    align-items: center;
    min-height: 56px;
    border: 1px solid #d2d2d2;
    border-radius: 8px;
    background: $white;
    padding: 0 15px;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;
  }

  &__captcha-field:focus-within {
    border-color: #6f9a78;
    box-shadow: 0 0 0 2px rgba(111, 154, 120, 0.12);
  }

  &__captcha-field.has-error {
    border-color: #dc3545;
  }

  &__captcha-field input {
    width: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: #3f3f46;
    font-size: 16px;
    line-height: 1.4;
  }

  &__captcha-field input::placeholder {
    color: #9b9b9b;
    font-size: 16px;
  }

  &__captcha-image {
    width: 106px;
    height: 56px;
    border-radius: 7px;
    object-fit: cover;
  }

  &__captcha-error {
    grid-column: 1 / -1;
    margin: 0;
    color: #dc3545;
    font-size: 12px;
    line-height: 1.4;
  }

  &__submit {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 131px;
    min-height: 38px;
    margin: 22px auto 0;
    border: 0;
    border-radius: 4px;
    background: #6f9876;
    color: $white;
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
  }

  &__submit:hover {
    background: #638a6a;
  }
}

@media (max-width: 768px) {
  .c-footer-newsletter-modal {
    .e-modal__content.c-footer-newsletter-modal__modal {
      width: calc(100% - 24px);
    }

    &__panel {
      padding: 24px 18px;
    }

    &__close {
      right: 18px;
    }

    &__captcha {
      grid-template-columns: minmax(0, 1fr) 96px;
      gap: 10px;
    }

    &__captcha-image {
      width: 96px;
    }
  }
}
</style>
